import { useEffect, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import type { PickleballGameListItem } from '@/services/pickleballService';
import PickleballScorePad from './PickleballScorePad';
import {
  applyScoreValue,
  buildScorePayload,
  buildSetsFromGame,
  canFillSet,
  scoringCap,
  scoringTarget,
  type SetScoreInput,
} from './pickleballScoringUtils';
import { formatPlayerFormat, playersLabel } from './pickleballUiUtils';

interface PickleballScoreSheetProps {
  open: boolean;
  game: PickleballGameListItem | null;
  gameNumber: number;
  saving: boolean;
  onClose: () => void;
  onSave: (sets: { setNumber: number; scoreSideA: number; scoreSideB: number }[]) => void;
}

export default function PickleballScoreSheet({
  open,
  game,
  gameNumber,
  saving,
  onClose,
  onSave,
}: PickleballScoreSheetProps) {
  const [sets, setSets] = useState<SetScoreInput[]>([]);
  const [activeSet, setActiveSet] = useState(1);

  useEffect(() => {
    if (!open || !game) return;
    setSets(buildSetsFromGame(game.maxSets, game.setScores ?? []));
    setActiveSet(1);
  }, [open, game]);

  if (!game) return null;
  const scoringType = game.scoringType || 'rally_21';
  const current = sets.find((s) => s.setNumber === activeSet) || {
    setNumber: activeSet,
    scoreSideA: 0,
    scoreSideB: 0,
  };

  const setAbsoluteScore = (side: 'a' | 'b', value: number) => {
    if (!canFillSet(activeSet, sets, game.maxSets, scoringType)) return;
    const next = applyScoreValue(
      current.scoreSideA,
      current.scoreSideB,
      side,
      value,
      scoringCap(scoringType)
    );
    setSets((prev) => prev.map((s) => (s.setNumber === activeSet ? { ...s, ...next } : s)));
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl">
        <SheetHeader>
          <SheetTitle>Edit Skor · Game {gameNumber}</SheetTitle>
          <SheetDescription>
            {formatPlayerFormat(game.playerFormat)} · {game.maxSets} set · target {scoringTarget(scoringType)} poin
          </SheetDescription>
        </SheetHeader>
        <Card className="mt-4 flex flex-row items-center justify-between rounded-2xl border-0 bg-white px-4 py-3 shadow-[inset_0_0_0_1px_#E0E6ED]">
          <p className="min-w-0 flex-1 text-center text-sm font-extrabold leading-tight text-[#0E1726]">
            {playersLabel(game.sideAPlayers) || 'Sisi A'}
          </p>
          <span className="px-2 text-xs font-semibold text-gray-400">vs</span>
          <p className="min-w-0 flex-1 text-center text-sm font-extrabold leading-tight text-[#0E1726]">
            {playersLabel(game.sideBPlayers) || 'Sisi B'}
          </p>
        </Card>
        <PickleballScorePad
          maxSets={game.maxSets}
          activeSet={activeSet}
          sets={sets}
          current={current}
          scoringType={scoringType}
          onSelectSet={setActiveSet}
          onSetScore={setAbsoluteScore}
        />
        <Button
          type="button"
          disabled={saving}
          className="mt-5 h-12 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
          onClick={() => onSave(buildScorePayload(sets, scoringType))}
        >
          Simpan Skor
        </Button>
      </SheetContent>
    </Sheet>
  );
}
